import { Cloud, CloudFog, CloudLightning, CloudRain, CloudSun, Snowflake, Sun, Wind } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMatchWeather, weatherKind, WeatherKind } from '@/hooks/useMatchWeather';
import { cn } from '@/lib/utils';

const LABEL: Record<WeatherKind, string> = {
  clear: 'Clear', partly: 'Partly cloudy', cloudy: 'Overcast', fog: 'Fog',
  rain: 'Rain', snow: 'Snow', storm: 'Thunderstorm',
};

function WeatherIcon({ kind, windy }: { kind: WeatherKind; windy: boolean }) {
  const base = 'h-4 w-4';
  if (windy && (kind === 'clear' || kind === 'partly')) return <Wind className={cn(base, 'text-primary animate-weather-drift')} />;
  switch (kind) {
    case 'clear': return <Sun className={cn(base, 'text-warning animate-weather-spin')} />;
    case 'partly': return <CloudSun className={cn(base, 'text-warning animate-weather-drift')} />;
    case 'cloudy': return <Cloud className={cn(base, 'text-muted-foreground animate-weather-drift')} />;
    case 'fog': return <CloudFog className={cn(base, 'text-muted-foreground animate-weather-drift')} />;
    case 'snow': return <Snowflake className={cn(base, 'text-foreground animate-weather-spin')} />;
    case 'storm': return <CloudLightning className={cn(base, 'text-destructive animate-weather-flash')} />;
    default: return <CloudRain className={cn(base, 'text-primary animate-weather-drop')} />;
  }
}

interface Props { clubId?: string | null; matchDate: Date; variant?: 'pill' | 'icon'; className?: string; }

export function MatchWeatherBadge({ clubId, matchDate, variant = 'pill', className }: Props) {
  const { data } = useMatchWeather(clubId, matchDate);
  if (!data) return null;

  const kind = weatherKind(data.code);
  const windy = data.windSpeed > 25 || data.windGusts > 35;
  const wet = data.precipitationProbability >= 50 || kind === 'rain' || kind === 'storm';
  const isIcon = variant === 'icon';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Weather forecast: ${LABEL[kind]}, ${data.temperature}°C, ${data.precipitationProbability}% rain`}
          className={cn(
            'relative inline-flex items-center justify-center rounded-full border text-xs font-medium whitespace-nowrap transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring after:absolute after:-inset-2 after:content-[""]',
            isIcon ? 'h-7 w-7' : 'gap-1 px-2 h-6',
            wet ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-muted/30',
            className,
          )}
        >
          <WeatherIcon kind={kind} windy={windy} />
          {!isIcon && <span className="tabular-nums text-foreground">{data.temperature}°</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-2">
          <WeatherIcon kind={kind} windy={windy} />
          <span className="font-semibold">{LABEL[kind]}</span>
        </div>
        <dl className="grid grid-cols-2 gap-y-1 text-xs">
          <dt className="text-muted-foreground">Temperature</dt><dd className="text-right tabular-nums">{data.temperature}°C</dd>
          <dt className="text-muted-foreground">Feels like</dt><dd className="text-right tabular-nums">{data.apparent}°C</dd>
          <dt className="text-muted-foreground">Rain chance</dt><dd className="text-right tabular-nums">{data.precipitationProbability}%</dd>
          <dt className="text-muted-foreground">Wind / gusts</dt><dd className="text-right tabular-nums">{data.windSpeed} / {data.windGusts} km/h</dd>
        </dl>
        {(windy || wet) && (
          <p className="mt-2 text-xs text-muted-foreground">
            {wet ? 'Rain likely — outdoor courts may be wet. ' : ''}
            {windy ? 'Strong wind may affect lobs and high balls.' : ''}
          </p>
        )}
        <p className="mt-2 text-[12px] text-muted-foreground">Forecast at the club for the match start · Open-Meteo</p>
      </PopoverContent>
    </Popover>
  );
}
